import { merge, Observable } from 'rxjs';

import { CmsPageSection } from '../../../models/content';
import { ContentSectionsEditorComponent } from '../content-sections-editor.component';
import { BlockPreviewRequest } from './block-preview-request';

/**
 * What a host gives the preview panel (#263): when something may have changed, and the request for what it holds now.
 * The controller debounces `changes$`, renders `focus$` at once, and asks `current()` only then, so a host never
 * builds a request per keystroke.
 */
export interface BlockPreviewSource {
  /** An edit may have happened (debounced by the controller). */
  changes$: Observable<unknown>;
  /** What is being previewed changed (another block, another suburb): render without waiting. */
  focus$?: Observable<unknown>;
  /** The request for what the editor holds now; null when there is nothing to show yet. */
  current(): BlockPreviewRequest | null;
  /** What the panel says while there is nothing to show (default: "Click into a block to preview it."). */
  hint?: string;
}

/** Builds the request from the sections editor's blocks as Save would write them (toMap()) and its active block. */
export type SectionsRequestBuilder = (
  sections: Record<string, CmsPageSection>,
  activeId: string | null
) => BlockPreviewRequest | null;

/**
 * A sections editor as a preview source, used by every host of the editor: the Pages and Region editors (the active
 * block at the page's address), the suburb template editor and a suburb's inserted-block editor (#262). `focus` adds
 * the host's own immediate changes, like the template editor's "Preview as" suburb.
 */
export class SectionsEditorSource implements BlockPreviewSource {
  readonly changes$: Observable<unknown>;
  readonly focus$: Observable<unknown>;

  constructor(
    private editor: ContentSectionsEditorComponent,
    private build: SectionsRequestBuilder,
    readonly hint?: string,
    focus?: Observable<unknown>
  ) {
    this.changes$ = editor.edited;
    this.focus$ = focus ? merge(editor.activeChange, focus) : editor.activeChange;
  }

  current(): BlockPreviewRequest | null {
    return this.build(this.editor.toMap(), this.editor.activeSectionId);
  }
}
